/**
 * The chat command map (ADR-068's amendment "chat commands, Discord style", item 1; the author's rulings of
 * 2026-09-25), as data the parser and the stepper read. Every command the map names is here, open or reserved, so
 * nothing else takes its word: the open ones the page acts on, and the reserved ones it recognises, leaves off the
 * list and answers without sending. A command is one word after `/`, read whole and without regard to case; its
 * options are in the order the input steps through them. The server knows only the messages, never this map.
 * ADR-068's amendment "Global, Trade and Help" opens `/g`, `/t`, `/h`, `/join` and `/leave`, and ADR-101 decision 25 opens `/p`, decision 4 and the amendments of the row P2.13-T5 open `/invite`, `/remove`, `/promote` and `/grant` (and `/leave party`), and the map adds a GM's `/mute`
 * and `/unmute`, which every page recognises and none lists, since the server alone decides who may.
 */

/**
 * How an option is typed: a full name (two words), a family (the rest of the line, as `/block` reads it), text
 * (the rest of the line), a channel's word, a command's word or a duration (one word each).
 */
export type OptionKind = "name" | "family" | "text" | "channel" | "command" | "duration";

/**
 * One option of a command: its name as the syntax shows it, how it is typed, whether it may be left empty, and how
 * many words it takes when that differs from its kind's, as a mute's family takes one where a block's takes the rest
 * of the line.
 */
export interface CommandOption {
  readonly name: string;
  readonly kind: OptionKind;
  readonly optional: boolean;
  readonly words?: number;
}

/**
 * What a command is, as the page names it.
 */
export type CommandId = "whisper" | "reply" | "pose" | "block" | "unblock" | "blocks" | "clear" | "ping" | "afk" | "party" | "raid" | "guild" | "officer" | "region" | "global" | "trade" | "help" | "duel" | "join" | "leave" | "mute" | "unmute" | "who" | "invite" | "remove" | "promote" | "grant" | "challenge" | "commands";

/**
 * The channels a command can name, by id. A channel's name is the language files' (`chat.scopes.<id>`).
 */
export type CommandChannel = "party" | "raid" | "guild" | "officer" | "region" | "global" | "trade" | "help" | "duel";

/**
 * One command of the map: its words, the first the one the list shows; whether it is open now; the channel a
 * channel command names, for its answer while it is not open, or null; its options; and whether the `/` list
 * leaves it out though it is open, as it does a GM's command.
 */
export interface ChatCommand {
  readonly id: CommandId;
  readonly words: readonly string[];
  readonly open: boolean;
  readonly channel: CommandChannel | null;
  readonly options: readonly CommandOption[];
  readonly unlisted?: boolean;
}

const NAME: CommandOption = { name: "name", kind: "name", optional: false };
const MESSAGE: CommandOption = { name: "message", kind: "text", optional: false };
const FAMILY: CommandOption = { name: "family", kind: "family", optional: false };
const CHANNEL: CommandOption = { name: "channel", kind: "channel", optional: false };
const MUTED_CHANNEL: CommandOption = { name: "channel", kind: "channel", optional: true };

/**
 * A reserved channel command: its word, the channel it names, and a message to it.
 *
 * @param id The command.
 * @param word Its word.
 * @param channel The channel's id, which names it in the language files (`chat.scopes.<id>`).
 * @returns The command.
 */
function reservedChannel(id: CommandId, word: string, channel: CommandChannel): ChatCommand {
  return { id, words: [word], open: false, channel, options: [MESSAGE] };
}

/**
 * Every command of the map in the map's order: the open ones that came first, then Global, Trade and Help, the
 * commands that join and leave them and a GM's mute, then the reserved ones.
 */
export const CHAT_COMMANDS: readonly ChatCommand[] = [
  { id: "whisper", words: ["w", "whisper"], open: true, channel: null, options: [NAME, MESSAGE] },
  { id: "reply", words: ["re", "reply", "^"], open: true, channel: null, options: [MESSAGE] },
  { id: "pose", words: ["me"], open: true, channel: null, options: [{ name: "action", kind: "text", optional: false }] },
  { id: "block", words: ["block"], open: true, channel: null, options: [FAMILY] },
  { id: "unblock", words: ["unblock"], open: true, channel: null, options: [FAMILY] },
  { id: "blocks", words: ["blocks"], open: true, channel: null, options: [] },
  { id: "clear", words: ["clear"], open: true, channel: null, options: [] },
  { id: "ping", words: ["ping"], open: true, channel: null, options: [] },
  { id: "afk", words: ["afk"], open: true, channel: null, options: [] },
  { id: "global", words: ["g"], open: true, channel: "global", options: [MESSAGE] },
  { id: "trade", words: ["t"], open: true, channel: "trade", options: [MESSAGE] },
  { id: "help", words: ["h"], open: true, channel: "help", options: [MESSAGE] },
  { id: "party", words: ["p"], open: true, channel: "party", options: [MESSAGE] },
  { id: "join", words: ["join"], open: true, channel: null, options: [CHANNEL] },
  { id: "leave", words: ["leave"], open: true, channel: null, options: [CHANNEL] },
  { id: "invite", words: ["invite"], open: true, channel: null, options: [NAME] },
  { id: "remove", words: ["remove"], open: true, channel: null, options: [NAME] },
  { id: "promote", words: ["promote"], open: true, channel: null, options: [NAME] },
  { id: "grant", words: ["grant"], open: true, channel: null, options: [NAME, { name: "state", kind: "text", optional: true, words: 1 }] },
  { id: "mute", words: ["mute"], open: true, channel: null, options: [{ ...FAMILY, words: 1 }, { name: "duration", kind: "duration", optional: false }, MUTED_CHANNEL], unlisted: true },
  { id: "unmute", words: ["unmute"], open: true, channel: null, options: [{ ...FAMILY, words: 1 }, MUTED_CHANNEL], unlisted: true },
  reservedChannel("raid", "ra", "raid"),
  reservedChannel("guild", "gu", "guild"),
  reservedChannel("officer", "o", "officer"),
  reservedChannel("region", "r", "region"),
  reservedChannel("duel", "d", "duel"),
  { id: "who", words: ["who"], open: false, channel: null, options: [{ ...NAME, optional: true }] },
  { id: "challenge", words: ["duel"], open: false, channel: null, options: [NAME] },
  { id: "commands", words: ["help"], open: false, channel: null, options: [{ name: "command", kind: "command", optional: true }] },
];

/**
 * Every command by each of its words, in lower case.
 */
const BY_WORD: ReadonlyMap<string, ChatCommand> = new Map(CHAT_COMMANDS.flatMap((command) => command.words.map((word) => [word, command] as const)));

/**
 * The command a word names, whole and without regard to case.
 *
 * @param word The word after `/`.
 * @returns The command, or undefined when the map names none.
 * @example
 * commandNamed("R")?.id;
 * // "region"
 */
export function commandNamed(word: string): ChatCommand | undefined {
  return BY_WORD.get(word.toLowerCase());
}

/**
 * The open commands one of whose words starts with what is typed, without regard to case, in the map's order.
 * A reserved command and an unlisted one are never among them.
 *
 * @param partial The word typed after `/` so far.
 * @returns The commands.
 */
export function commandsStartingWith(partial: string): ChatCommand[] {
  const typed = partial.toLowerCase();
  return CHAT_COMMANDS.filter((command) => command.open && command.unlisted !== true && command.words.some((word) => word.startsWith(typed)));
}

/**
 * Where the player stands in a party, which decides what the `/` list offers: in none, in one as a member who does
 * not lead it, or leading one.
 */
export type PartyStanding = "none" | "member" | "leader";

/**
 * The groups of the `/` list, in the order it shows them; each group's heading is `chat.groups.<id>` in the
 * language files.
 */
export type CommandGroup = "chat" | "channels" | "party" | "social";

/**
 * The commands of each group of the `/` list.
 */
const GROUP_MEMBERS: Readonly<Record<CommandGroup, readonly CommandId[]>> = {
  chat: ["whisper", "reply", "pose", "clear", "afk", "ping"],
  channels: ["global", "trade", "help", "party", "join", "leave"],
  party: ["invite", "remove", "promote", "grant"],
  social: ["block", "unblock", "blocks"],
};

/**
 * The commands of each group, resolved once from the map.
 */
const GROUP_COMMANDS: Readonly<Record<CommandGroup, readonly ChatCommand[]>> = {
  chat: resolved("chat"),
  channels: resolved("channels"),
  party: resolved("party"),
  social: resolved("social"),
};

/**
 * The map's commands a group names, in the group's order.
 *
 * @param group The group.
 * @returns The commands.
 */
function resolved(group: CommandGroup): readonly ChatCommand[] {
  return GROUP_MEMBERS[group].flatMap((id) => CHAT_COMMANDS.filter((command) => command.id === id));
}

/**
 * The groups in the order the list shows them.
 */
export const COMMAND_GROUPS: readonly CommandGroup[] = ["chat", "channels", "party", "social"];

/**
 * The group of the `/` list a command is shown under.
 *
 * @param id The command.
 * @returns The group, or null for a command the list never shows.
 */
export function commandGroupOf(id: CommandId): CommandGroup | null {
  return COMMAND_GROUPS.find((group) => GROUP_MEMBERS[group].includes(id)) ?? null;
}

/**
 * Whether the `/` list offers a command to a player in a given standing: `/p` only in a party, `/invite` outside a
 * party and to its leader, and `/remove`, `/promote` and `/grant` to a leader. The list is a convenience; a command
 * typed whole is read and refused as ever.
 *
 * @param id The command.
 * @param standing The player's standing in a party.
 * @returns Whether the list shows it.
 */
function appliesTo(id: CommandId, standing: PartyStanding): boolean {
  switch (id) {
    case "party":
      return standing !== "none";
    case "invite":
      return standing !== "member";
    case "remove":
    case "promote":
    case "grant":
      return standing === "leader";
    default:
      return true;
  }
}

/**
 * One entry of the `/` list: a command and the group it is shown under.
 */
export interface ListedCommand {
  readonly command: ChatCommand;
  readonly group: CommandGroup;
}

/**
 * The commands the `/` list shows for a partial word and a standing: the open, listed ones that apply, one of whose
 * words starts with what is typed, grouped in the groups' order and alphabetical by the word the list shows within
 * a group.
 *
 * @param partial The word typed after `/` so far.
 * @param standing The player's standing in a party.
 * @returns The entries; a group with none has none.
 * @example
 * listedCommands("p", "none").map((entry) => entry.command.id);
 * // ["ping"]
 */
export function listedCommands(partial: string, standing: PartyStanding): ListedCommand[] {
  const starting = new Set(commandsStartingWith(partial));
  return COMMAND_GROUPS.flatMap((group) =>
    GROUP_COMMANDS[group]
      .filter((command) => starting.has(command) && appliesTo(command.id, standing))
      .sort((a, b) => (a.words[0] ?? "").localeCompare(b.words[0] ?? "", "en"))
      .map((command) => ({ command, group })),
  );
}

/**
 * A command's options as its syntax: each option's name, an optional one in brackets, in order.
 *
 * @param command The command.
 * @returns The syntax, empty for a command with no options.
 * @example
 * optionSyntax(commandNamed("w")!);
 * // "name message"
 */
export function optionSyntax(command: ChatCommand): string {
  return command.options.map((option) => (option.optional ? `[${option.name}]` : option.name)).join(" ");
}
